import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Column, DataTable } from "@/components/ui/data-table";

export type GroupUserRow = {
  id: string;
  name: string;
  role: string;
  hotelGroup: string;
  initials: string;
};

type GroupCreateUsersTableProps = {
  rows: GroupUserRow[];
  selectedUserIds: string[];
  areAllVisibleSelected: boolean;
  onToggleUser: (id: string) => void;
  onToggleAllVisible: (checked: boolean) => void;
  contextColumnHeader?: string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
};

export const GroupCreateUsersTable: React.FC<GroupCreateUsersTableProps> = ({
  rows,
  selectedUserIds,
  areAllVisibleSelected,
  onToggleUser,
  onToggleAllVisible,
  contextColumnHeader,
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const columns: Column<GroupUserRow>[] = [
    {
      key: "selection",
      header: (
        <div className="flex h-full items-center justify-center">
          <Checkbox
            checked={areAllVisibleSelected}
            onCheckedChange={(checked) => onToggleAllVisible(Boolean(checked))}
            disabled={disabled}
          />
        </div>
      ),
      className: "w-[56px] py-0 text-center align-middle",
      render: (_value, row) => (
        <div className="flex h-full items-center justify-center">
          <Checkbox
            checked={selectedUserIds.includes(row.id)}
            onCheckedChange={() => onToggleUser(row.id)}
            disabled={disabled}
          />
        </div>
      ),
    },
    {
      key: "name",
      header: t("users.groups.table.name", "Name"),
      sortable: false,
      className: "py-2",
      render: (_value, row) => (
        <div className="flex items-center gap-3 py-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E4E4E7] text-[11px] font-semibold text-[#71717A]">
            {row.initials}
          </div>
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold text-[#18181B]">
              {row.name}
            </span>
            <span className="text-[12px] text-[#71717A]">{row.role}</span>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: t("users.table.role", "Role"),
      sortable: false,
      className: "py-2",
      render: (value) => (
        <span className="text-[13px] text-[#52525B]">{value}</span>
      ),
    },
    {
      key: "hotelGroup",
      header: contextColumnHeader ?? t("users.table.hotelGroup", "Hotel Group"),
      sortable: false,
      className: "py-2",
      render: (value) => (
        <span className="text-[13px] text-[#52525B]">{value}</span>
      ),
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      loading={isLoading}
      getRowKey={(row) => row.id}
      columnHeaderClassName="inline-flex h-10 items-center gap-1 px-2 font-sans text-[10px] font-medium leading-4 tracking-normal text-neutral-500"
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      withSorting={false}
      emptyMessage={t("users.groups.table.noGroupsFound", "No groups found")}
    />
  );
};
